import React, { FC, useMemo } from "react";
import { ListItemIcon, ListItemText, useTheme } from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import { Palette } from "@green0meter/api-client";
import { useTranslation } from "react-i18next";

import { CustomColors } from "@type/CustomPalette";
import { useCustomThemeStore } from "@store/customTheme";
import { useGetCurrentCompanyInfo, useUpdateCompanyPalette } from "@api";
import {
  Autocomplete,
  Box,
  FormControl,
  InputAdornment,
  TextField,
} from "@components/base";
import { useUserStore } from "@store/user";

type ThemeColorOption = {
  value: CustomColors | "default";
  label: string;
  circleSxColor: string;
};

const ThemeSettings: FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { color, setColor } = useCustomThemeStore();
  const { currentCompanyInfo, currentCompanyInfoEnabled } =
    useGetCurrentCompanyInfo(user);
  const { updateCompanyPalette, updateCompanyPaletteEnabled } =
    useUpdateCompanyPalette(user);

  const options = useMemo<ThemeColorOption[]>(
    () => [
      {
        value: "default",
        label: t("themeSettings.default"),
        circleSxColor: theme.palette.custom.navy[700],
      },
      {
        value: "blue" as CustomColors,
        label: t("themeSettings.blue"),
        circleSxColor: theme.palette.custom.blue[700],
      },
      {
        value: "grey" as CustomColors,
        label: t("themeSettings.grey"),
        circleSxColor: theme.palette.custom.grey[700],
      },
      {
        value: "purple" as CustomColors,
        label: t("themeSettings.purple"),
        circleSxColor: theme.palette.custom.purple[700],
      },
      {
        value: "green" as CustomColors,
        label: t("themeSettings.green"),
        circleSxColor: theme.palette.custom.green[700],
      },
      {
        value: "pink" as CustomColors,
        label: t("themeSettings.pink"),
        circleSxColor: theme.palette.custom.pink[500],
      },
      {
        value: "deepBlue" as CustomColors,
        label: t("themeSettings.deepBlue"),
        circleSxColor: theme.palette.custom.deepBlue[500],
      },
    ],
    [t, theme],
  );

  const selectedOption =
    options.find((option) => option.value === (color ?? "default")) ?? options[0];

  const handleChange = (_: unknown, option: ThemeColorOption | null): void => {
    const next = (option?.value ?? "default") as CustomColors;

    setColor(next);

    if (currentCompanyInfo) {
      updateCompanyPalette({
        companyId: currentCompanyInfo.id,
        color: next as Palette,
      });
    }
  };

  if (!currentCompanyInfoEnabled) return null;

  return (
    <FormControl fullWidth>
      <Autocomplete<ThemeColorOption, false, true, false>
        disableClearable
        value={selectedOption}
        options={options}
        onChange={handleChange}
        disabled={!updateCompanyPaletteEnabled}
        isOptionEqualToValue={(a, b) => a.value === b.value}
        getOptionLabel={(o) => o.label}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("themeSettings.chooseColor")}
            placeholder={t("themeSettings.chooseColor")}
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start" sx={{ mr: 1 }}>
                      <CircleIcon sx={{ color: selectedOption.circleSxColor }} />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CircleIcon sx={{ color: option.circleSxColor }} />
            </ListItemIcon>
            <ListItemText>{option.label}</ListItemText>
          </li>
        )}
      />
    </FormControl>
  );
};

export default ThemeSettings;
